import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Boxes, X, Send, Check, Zap, Shield, TruckIcon } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { products } from '../data/mockData';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bulk product catalog — 10 vials each
const BULK_PRODUCTS = [
  { productId: 1,  name: 'Reta 10mg',          vials: 10, price: 300 },
  { productId: 2,  name: 'Reta 15mg',          vials: 10, price: 375 },
  { productId: 3,  name: 'Reta 20mg',          vials: 10, price: 425 },
  { productId: 25, name: 'Tirzepatide 15mg',   vials: 10, price: 275 },
  { productId: 26, name: 'Tirzepatide 20mg',   vials: 10, price: 350 },
  { productId: 34, name: 'Tirzepatide 35mg',   vials: 10, price: 450 },
  { productId: 10, name: 'Wolverine Stack',    vials: 10, price: 375 },
  { productId: 15, name: 'Glow',               vials: 10, price: 500 },
];

const getProductImage = (id) => {
  const p = products.find((x) => x.id === id);
  return p?.imageUrl || '';
};

const BulkInquiryModal = ({ item, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError('Please fill in your name, email and phone.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bulk-inquiries`, {
        product_id: item.productId,
        product_name: item.name,
        tier: 'bulk_10',
        name: name.trim(),
        contact: `${email.trim()} | ${phone.trim()}`,
        email: email.trim(),
        phone: phone.trim(),
        preferred_quantity: `10 vials — $${item.price}`,
        message: message.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or text 562-292-1095.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="bulk-inquiry-modal"
    >
      <div
        className="w-full max-w-lg rounded-lg border shadow-2xl"
        style={{ backgroundColor: '#1A1513', borderColor: '#362B25' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: '#362B25' }}
        >
          <div>
            <div
              className="text-[11px] tracking-[0.2em] uppercase font-semibold"
              style={{ color: '#D4A373' }}
            >
              10-Vial Bulk Pack
            </div>
            <h3
              className="text-xl font-medium"
              style={{ fontFamily: 'Playfair Display, serif', color: '#F7F3EE' }}
            >
              {item.name} — ${item.price}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-[#0C0A09] transition"
            style={{ color: '#B5A79E' }}
            data-testid="bulk-inquiry-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {submitted ? (
          <div className="px-6 py-10 text-center">
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
              style={{ backgroundColor: '#D4A373' }}
            >
              <Check className="h-7 w-7" style={{ color: '#0C0A09' }} strokeWidth={2.5} />
            </div>
            <h4
              className="text-xl font-medium mb-2"
              style={{ fontFamily: 'Playfair Display, serif', color: '#F7F3EE' }}
            >
              Request received
            </h4>
            <p className="text-sm mb-6" style={{ color: '#B5A79E' }}>
              We'll reach out shortly to lock in your bulk order and confirm payment details.
            </p>
            <Button
              onClick={onClose}
              className="px-6"
              style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}
              data-testid="bulk-inquiry-done"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-6 space-y-4">
            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1.5"
                style={{ color: '#D4A373' }}
              >
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                required
                data-testid="bulk-inquiry-name"
                className="w-full px-4 py-3 rounded border text-[#F7F3EE] placeholder:text-[#7A6E66] focus:outline-none focus:border-[#D4A373]"
                style={{ backgroundColor: '#0C0A09', borderColor: '#362B25' }}
              />
            </div>

            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1.5"
                style={{ color: '#D4A373' }}
              >
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="bulk-inquiry-email"
                className="w-full px-4 py-3 rounded border text-[#F7F3EE] placeholder:text-[#7A6E66] focus:outline-none focus:border-[#D4A373]"
                style={{ backgroundColor: '#0C0A09', borderColor: '#362B25' }}
              />
            </div>

            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1.5"
                style={{ color: '#D4A373' }}
              >
                Phone Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                required
                data-testid="bulk-inquiry-phone"
                className="w-full px-4 py-3 rounded border text-[#F7F3EE] placeholder:text-[#7A6E66] focus:outline-none focus:border-[#D4A373]"
                style={{ backgroundColor: '#0C0A09', borderColor: '#362B25' }}
              />
            </div>

            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1.5"
                style={{ color: '#D4A373' }}
              >
                Notes (optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Anything we should know? Preferred payment, timing, delivery preference…"
                data-testid="bulk-inquiry-message"
                className="w-full px-4 py-3 rounded border text-[#F7F3EE] placeholder:text-[#7A6E66] focus:outline-none focus:border-[#D4A373] resize-none"
                style={{ backgroundColor: '#0C0A09', borderColor: '#362B25' }}
              />
            </div>

            {error && (
              <p className="text-sm text-red-400" data-testid="bulk-inquiry-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full py-6 text-base font-semibold tracking-wide"
              style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}
              data-testid="bulk-inquiry-submit"
            >
              {submitting ? (
                'Sending…'
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Bulk Request
                </>
              )}
            </Button>

            <p className="text-xs text-center" style={{ color: '#7A6E66' }}>
              We reply within a few hours. No charge happens on this site.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

const BulkCard = ({ item, onBuy }) => {
  const image = getProductImage(item.productId);
  return (
    <div
      className="group relative rounded-lg border overflow-hidden transition-all duration-300 hover:border-[#D4A373]"
      style={{ backgroundColor: '#1A1513', borderColor: '#362B25' }}
      data-testid={`bulk-card-${item.productId}`}
    >
      {/* 10 VIAL ribbon */}
      <div
        className="absolute top-3 left-3 z-10 px-3 py-1 rounded text-[10px] font-bold tracking-[0.2em] uppercase"
        style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}
      >
        10-Vial Pack
      </div>

      {/* Image */}
      <div
        className="aspect-square flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#0C0A09' }}
      >
        {image ? (
          <img
            src={image}
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <Boxes className="h-16 w-16" style={{ color: '#D4A373' }} strokeWidth={1.5} />
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col gap-4">
        <div>
          <h3
            className="text-xl font-medium leading-tight"
            style={{ fontFamily: 'Playfair Display, serif', color: '#F7F3EE' }}
          >
            {item.name}
          </h3>
          <p
            className="text-xs mt-1 tracking-wider uppercase"
            style={{ color: '#B5A79E' }}
          >
            {item.vials} Vials
          </p>
        </div>

        <div className="flex items-baseline gap-1.5">
          <span
            className="text-3xl font-semibold"
            style={{ fontFamily: 'Playfair Display, serif', color: '#D4A373' }}
          >
            ${item.price}
          </span>
          <span className="text-sm" style={{ color: '#7A6E66' }}>
            / pack
          </span>
        </div>

        <Button
          onClick={() => onBuy(item)}
          className="w-full py-5 font-semibold tracking-wide"
          style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}
          data-testid={`bulk-buy-btn-${item.productId}`}
        >
          BUY BULK
        </Button>
      </div>
    </div>
  );
};

const BulkPricing = () => {
  const [active, setActive] = useState(null);

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: '#0C0A09' }}>
      <Seo
        path="/bulk"
        title="Bulk Peptide Packs | 10 Vial Wholesale | Eman's Peppers"
        description="Save with 10-vial bulk packs from Eman's Peppers — Reta, Tirzepatide, Wolverine Stack, and Glow at flat wholesale pricing. Perfect for labs and long research protocols."
      />
      {/* Hero */}
      <section
        className="relative py-20 md:py-24 overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, #1A1513 0%, #0C0A09 50%, #1A1513 100%)',
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #D4A373 0%, transparent 40%), radial-gradient(circle at 80% 70%, #D4A373 0%, transparent 40%)',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/products"
            className="inline-flex items-center text-sm mb-6 hover:opacity-80 transition"
            style={{ color: '#B5A79E' }}
            data-testid="bulk-back-link"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to Products
          </Link>

          <div
            className="inline-block px-4 py-1.5 rounded-full text-[11px] tracking-[0.25em] uppercase font-semibold mb-6"
            style={{
              backgroundColor: 'rgba(212, 163, 115, 0.15)',
              color: '#D4A373',
              border: '1px solid rgba(212, 163, 115, 0.4)',
            }}
          >
            Wholesale • Direct Pricing
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] mb-5"
            style={{ fontFamily: 'Playfair Display, serif', color: '#F7F3EE' }}
          >
            10 <span style={{ color: '#D4A373' }}>Vial</span> Bulk Packs
          </h1>

          <p
            className="text-base md:text-lg max-w-2xl leading-relaxed"
            style={{ color: '#B5A79E' }}
          >
            Serious volume. Serious savings. Lock in your favorite research
            peptides — 10 vials of the same product, one flat price. Perfect for
            labs, groups, and long-run protocols.
          </p>

          {/* Trust strip */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            {[
              { icon: Zap, label: 'Direct wholesale pricing' },
              { icon: Shield, label: 'Same lab, same quality' },
              { icon: TruckIcon, label: 'Priority handling' },
            ].map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 rounded border"
                style={{ backgroundColor: '#1A1513', borderColor: '#362B25' }}
              >
                <f.icon
                  className="h-5 w-5 flex-shrink-0"
                  style={{ color: '#D4A373' }}
                  strokeWidth={1.5}
                />
                <span className="text-sm" style={{ color: '#F7F3EE' }}>
                  {f.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BULK_PRODUCTS.map((item) => (
              <BulkCard key={item.productId} item={item} onBuy={setActive} />
            ))}
          </div>

          {/* Bottom CTA */}
          <div
            className="mt-16 p-8 md:p-10 rounded-lg border text-center"
            style={{
              backgroundColor: '#1A1513',
              borderColor: '#362B25',
              backgroundImage:
                'radial-gradient(circle at 50% 0%, rgba(212, 163, 115, 0.08) 0%, transparent 60%)',
            }}
          >
            <h2
              className="text-2xl md:text-3xl font-medium mb-3"
              style={{ fontFamily: 'Playfair Display, serif', color: '#F7F3EE' }}
            >
              Need a custom bulk mix?
            </h2>
            <p
              className="text-sm md:text-base max-w-2xl mx-auto mb-6"
              style={{ color: '#B5A79E' }}
            >
              Looking for a product not listed, or want a mixed 10-vial pack?
              Text us and we'll build a custom quote.
            </p>
            <a
              href="sms:+15622921095?&body=Hi,%20I'd%20like%20a%20custom%20bulk%20quote%20—"
              className="inline-flex items-center gap-2 px-8 py-3 rounded font-semibold tracking-wide transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}
              data-testid="bulk-custom-sms"
            >
              Text 562-292-1095
            </a>
          </div>
        </div>
      </section>

      {active && (
        <BulkInquiryModal item={active} onClose={() => setActive(null)} />
      )}
    </div>
  );
};

export default BulkPricing;
